export function formatDateTimeLocal(date) {
    const pad = (n) => (n < 10 ? '0' + n : n);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Format a UTC Date as a datetime-local input string (YYYY-MM-DDTHH:mm)
 * rendered in the specified IANA timezone.
 *
 * @param {Date} date - UTC Date object
 * @param {string} timezone - IANA timezone (e.g. 'America/New_York', 'UTC')
 * @returns {string} value suitable for <input type="datetime-local">
 */
export function formatForTzInput(date, timezone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(date)

    const get = (type) => parts.find(p => p.type === type)?.value ?? '00'
    let hour = get('hour')
    // Some environments emit '24' for midnight with hour12: false
    if (hour === '24') hour = '00'
    return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`
}

/**
 * Parse a datetime-local string (YYYY-MM-DDTHH:mm) that the user entered in
 * the given IANA timezone and return the equivalent UTC ISO 8601 string.
 *
 * Algorithm: treat the naive local value as-if-UTC, measure how far the
 * target timezone lies from UTC at that moment, then shift accordingly.
 *
 * @param {string} localStr - datetime-local string, e.g. '2024-03-15T14:32'
 * @param {string} timezone - IANA timezone (e.g. 'America/New_York', 'UTC')
 * @returns {string} UTC ISO string
 */
export function parseFromTzInput(localStr, timezone) {
    if (!localStr) return ''
    const [datePart, timePart = '00:00'] = localStr.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    const [hour, minute] = timePart.split(':').map(Number)

    // Step 1: treat the local time as if it were UTC
    const asIfUTC = new Date(Date.UTC(year, month - 1, day, hour, minute))

    // Step 2: find what that UTC instant looks like in the target timezone
    const tzParts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(asIfUTC)

    const tzGet = (type) => parseInt(tzParts.find(p => p.type === type)?.value ?? '0', 10)
    const tzHour = tzGet('hour') === 24 ? 0 : tzGet('hour')
    const tzAsUTC = new Date(Date.UTC(tzGet('year'), tzGet('month') - 1, tzGet('day'), tzHour, tzGet('minute')))

    // Step 3: the timezone offset at this moment is (asIfUTC - tzAsUTC).
    //         Adding it back gives us the true UTC for the user's local time.
    const offsetMs = asIfUTC.getTime() - tzAsUTC.getTime()
    return new Date(asIfUTC.getTime() + offsetMs).toISOString()
}